import { defineStore } from "pinia";
import { generateCacheKey, clearCache } from "@/composables/useCache";
import { store } from "@/stores/global.js";
import { doFetch } from "@/composables/doFetch";

export const useMoversStore = defineStore("movers", {
    state: () => ({
        moversData: {},
        isLoading: false,
        lastFetchTime: {},
        fetchInProgress: {},
        cacheTimeMs: 60000, // 1 minute cache (market movers change frequently)
    }),

    getters: {
        /**
         * Get movers data for a specific combination of parameters
         */
        getMovers: state => (stat, country, marketcapSize, exchange, statTop) => {
            // Use the standard generateCacheKey function instead of join("_")
            const cacheKey = generateCacheKey(
                stat,
                country,
                marketcapSize || "all",
                exchange || "all",
                statTop || "all"
            );
            return state.moversData[cacheKey] || [];
        },
    },
    actions: {
        /**
         * Direct API call to fetch movers data
         * @param {Object} params - API parameters
         * @param {string} token - Authentication token
         * @returns {Promise<Object>} - API response
         */
        async callMoversAPI(params, token) {
            const url = new URL(`https://${store.env}.quotemedia.com/datatool/getMarketStats.json`);

            url.searchParams.append("webmaster_id", store.wmid);
            url.searchParams.append("webmasterId", store.wmid);
            url.searchParams.append("qmodTool", "MiniQuotes"); // Use component name as tool
            url.searchParams.append("pathName", window.location.pathname);
            if (store.sid) url.searchParams.append("sid", store.sid);

            // Add all the parameters
            for (const key in params) {
                if (params[key] != null) {
                    url.searchParams.append(key, params[key]);
                }
            }

            return await doFetch(url, {
                method: "GET",
                headers: {
                    "Accept-Language": "en",
                    "Content-Type": "application/json",
                    "Datatool-Token": token,
                },
            });
        } /**
         * Fetch market movers data
         * @param {string} stat - Stat type (va, dv, dg, dl, pg, pl, ah, al, plvc)
         * @param {string} token - Authentication token for the API call
         * @param {string} statCountry - Country code (US, CA)
         * @param {boolean} extraHours - Whether to include extra hours data (premarket, afterhours)
         * @param {string} marketcapSize - Market capitalization size filter
         * @param {string} exchange - Exchange code to filter by
         * @param {number} statTop - Number of top movers to return
         * @param {boolean} force - Force refresh even if cached
         * @returns {Promise<Array>} - Array of mover data
         */,
        async fetchMovers(
            stat = "va",
            token,
            statCountry = "US",
            extraHours = false,
            marketcapSize = null,
            exchange = null,
            statTop = null,
            force = false
        ) {
            if (!token) throw new Error("Token is required for API calls");
            // Use the standard generateCacheKey function instead of join("_")
            const cacheKey = generateCacheKey(
                stat,
                statCountry,
                marketcapSize || "all",
                exchange || "all",
                statTop || "all",
                extraHours || false
            );

            // If already fetching this data, wait for that request
            if (this.fetchInProgress[cacheKey]) {
                return this.fetchInProgress[cacheKey];
            }

            // Check if we need to fetch (not cached or force refresh)
            const now = Date.now();
            const needsFetch =
                force ||
                !this.moversData[cacheKey] ||
                !this.lastFetchTime[cacheKey] ||
                now - this.lastFetchTime[cacheKey] > this.cacheTimeMs;

            if (!needsFetch) {
                return this.moversData[cacheKey];
            }

            // Create a new fetch promise and store it
            const fetchPromise = (async () => {
                try {
                    this.isLoading = true;
                    const params = {
                        stat,
                        marketcapSize,
                        statTop,
                        afterhours: extraHours,
                        premarket: extraHours,
                    };

                    // Exchange takes priority over country if both are provided
                    if (exchange) {
                        params.statExchange = exchange;
                    } else {
                        // Only use country if no exchange is specified
                        params.statCountry = statCountry;
                    }

                    const response = await this.callMoversAPI(params, token);

                    // Handle error response from doFetch
                    if (response.error) {
                        throw new Error(`API call failed: ${response.error}`);
                    }

                    let moversData = [];

                    if (response && response.results && response.results.quote) {
                        moversData = response.results.quote.map(quote =>
                            this.formatMoverQuote(quote)
                        );
                    }

                    this.moversData[cacheKey] = moversData;
                    this.lastFetchTime[cacheKey] = now;
                    return moversData;
                } finally {
                    this.isLoading = false;
                    delete this.fetchInProgress[cacheKey];
                }
            })();

            this.fetchInProgress[cacheKey] = fetchPromise;
            return fetchPromise;
        },

        /**
         * Format a single mover quote from API response
         * @param {Object} quote - Raw quote data
         * @returns {Object} - Formatted quote data for display
         */
        formatMoverQuote(quote) {
            if (!quote) return null;

            // Start with the regular price data
            let priceData = quote.pricedata;

            // Determine data source based on what's available
            let dataSource = "regular";

            // Initialize change values
            let totalChange = 0;
            let totalChangePercent = 0;

            // Use parseFloat and isNaN to safely handle numeric values
            let last = 0;
            let volume = parseFloat(priceData?.sharevolume) || 0;

            // Priority for last price: afterhours > regular > premarket
            if (quote.afterhours?.last !== undefined) {
                last = parseFloat(quote.afterhours.last) || 0;
                volume = parseFloat(quote.afterhours.sharevolume) || volume;
                dataSource = "afterhours";
            } else if (priceData?.last !== undefined) {
                last = parseFloat(priceData.last) || 0;
                dataSource = "regular";
            } else if (quote.premarket?.last !== undefined) {
                last = parseFloat(quote.premarket.last) || 0;
                volume = parseFloat(quote.premarket.sharevolume) || volume;
                dataSource = "premarket";
            }

            // Calculate change based on data source
            if (dataSource === "regular") {
                // For regular market hours, use only the regular session change
                if (priceData?.change !== undefined) {
                    totalChange = parseFloat(priceData.change) || 0;
                }

                if (priceData?.changepercent !== undefined) {
                    totalChangePercent = parseFloat(priceData.changepercent) || 0;
                }
            } else if (dataSource === "afterhours") {
                // For after-hours, add after-hours change to regular market change
                if (priceData?.change !== undefined) {
                    totalChange = parseFloat(priceData.change) || 0;
                }

                if (quote.afterhours?.change !== undefined) {
                    totalChange += parseFloat(quote.afterhours.change) || 0;
                }

                // Calculate percent change based on previous close if available
                const prevClosePrice =
                    priceData?.prevclose !== undefined
                        ? parseFloat(priceData.prevclose) || 0
                        : null;

                if (prevClosePrice > 0 && last > 0) {
                    totalChangePercent = (totalChange / prevClosePrice) * 100;
                } else {
                    // Fall back to adding percent changes
                    if (priceData?.changepercent !== undefined) {
                        totalChangePercent = parseFloat(priceData.changepercent) || 0;
                    }
                    if (quote.afterhours?.changepercent !== undefined) {
                        totalChangePercent += parseFloat(quote.afterhours.changepercent) || 0;
                    }
                }
            } else if (dataSource === "premarket") {
                // For premarket, use premarket change only
                if (quote.premarket?.change !== undefined) {
                    totalChange = parseFloat(quote.premarket.change) || 0;
                }

                if (quote.premarket?.changepercent !== undefined) {
                    totalChangePercent = parseFloat(quote.premarket.changepercent) || 0;
                }
            }

            const isPositive = totalChange >= 0;

            return {
                symbol: quote.key?.symbol || "",
                longName: quote.equityinfo?.longname || "",
                shortName: quote.equityinfo?.shortname || "",
                last,
                change: totalChange,
                changePercent: totalChangePercent,
                changeDirection: isPositive ? "positive" : "negative",
                changeSign: isPositive ? "+" : "",
                volume,
                volumeChangePercent: parseFloat(priceData?.volumechangepercent) || 0,
                marketCap: parseFloat(quote.fundamental?.marketcap) || 0,
                dataSource,
                prevClose:
                    priceData?.prevclose !== undefined
                        ? parseFloat(priceData.prevclose) || 0
                        : null,
                rawData: quote, // Keep the original data
            };
        },

        /**
         * Clear cached data for specific parameters or all data
         * @param {string|null} stat - Stat to clear or null for all
         * @param {string|null} country - Country to clear or null for all
         * @param {string|null} marketcapSize - Market cap size to clear or null for all
         * @param {string|null} exchange - Exchange to clear or null for all
         * @param {number|null} statTop - Top count to clear or null for all
         */
        clearCache(
            stat = null,
            country = null,
            marketcapSize = null,
            exchange = null,
            statTop = null
        ) {
            // Create filters object based on parameters
            const filters = {};
            if (stat !== null) filters[0] = stat;
            if (country !== null) filters[1] = country;
            if (marketcapSize !== null) filters[2] = marketcapSize || "all";
            if (exchange !== null) filters[3] = exchange || "all";
            if (statTop !== null) filters[4] = String(statTop) || "all";

            clearCache(
                {
                    dataCache: this.moversData,
                    timeCache: this.lastFetchTime,
                },
                {
                    separator: "|", // Use the standard separator
                    filters: Object.keys(filters).length > 0 ? filters : null,
                }
            );
        },
    },
});
